import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAvailableCompanies } from '@/hooks/useAvailableCompanies';
import { useAuth } from '@/contexts/AuthContext';
import {
  COMPANY_FILTER_STORAGE_KEY,
  pageStateIdentity,
} from '@/utils/pageStateOwner';

/**
 * Rotas onde o Header renderiza o seletor global de EMPRESA — nas demais
 * telas o controle seria inerte (o backend ignora o filtro fora do
 * painel/planilha).
 * A planilha (/planilha-processos) NÃO entra: ela não usa o componente
 * Header e expõe o mesmo filtro pela FiltersBar. Um seletor por tela.
 */
export const COMPANY_FILTER_ROUTES: RegExp[] = [
  /^\/painel-aduaneiro/,
  // Só o painel de PO e a aba de prazos: item, planilha e detalhe não leem o filtro.
  /^\/purchase-orders\/?$/,
  /^\/purchase-orders\/prazos\/?$/,
];

interface CompanyFilterContextType {
  selectedCompanyIds: number[];
  /**
   * Seleção normalizada (ordenada + join ','): [43,42] ≡ [42,43]. Usar em
   * queryKeys/payloads em vez de join cru — espelha o `selectionKey` do
   * backend e evita refetch por reordenação.
   */
  companySelectionKey: string;
  setSelectedCompanyIds: (ids: number[]) => void;
  availableCompanies: { id: number; name: string }[];
  showSelector: boolean;
}

const CompanyFilterContext = createContext<
  CompanyFilterContextType | undefined
>(undefined);

function readStored(): number[] {
  try {
    const raw = localStorage.getItem(COMPANY_FILTER_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((n) => Number.isFinite(n)) : [];
  } catch {
    return [];
  }
}

export const CompanyFilterProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { options, isLoading } = useAvailableCompanies();
  const { user } = useAuth();
  const [rawSelected, setRawSelected] = useState<number[]>(readStored);

  // Re-hidrata quando a identidade muda (login/logout no mesmo tab, "ver como
  // externo"): o provider monta uma vez no topo do App, então sem isso a
  // seleção do usuário anterior sobrevive EM MEMÓRIA mesmo depois de
  // ensurePageStateOwner purgar o localStorage no login do próximo.
  const identity = user ? pageStateIdentity(user) : '';
  useEffect(() => {
    setRawSelected(readStored());
  }, [identity]);

  const setSelectedCompanyIds = useCallback((ids: number[]) => {
    setRawSelected(ids);
    try {
      localStorage.setItem(COMPANY_FILTER_STORAGE_KEY, JSON.stringify(ids));
    } catch {
      // Storage cheio / Safari privado: segue só em memória, não pode
      // derrubar o handler por isso.
    }
  }, []);

  // Poda ids que não estão mais entre as opções (empresa removida do
  // vínculo). Durante o load, options=[] não pode zerar a seleção persistida
  // — mas TERMINADO o load sem opções (fetch falhou, usuário caiu para 1
  // empresa), a seleção antiga é descartada: sem opções não há seletor na
  // tela, e uma seleção aplicada sem controle visível filtra o painel em
  // silêncio.
  const selectedCompanyIds = useMemo(() => {
    if (options.length === 0) return isLoading ? rawSelected : [];
    const valid = new Set(options.map((o) => o.id));
    return rawSelected.filter((id) => valid.has(id));
  }, [rawSelected, options, isLoading]);

  useEffect(() => {
    if (options.length > 0 && selectedCompanyIds.length !== rawSelected.length) {
      setSelectedCompanyIds(selectedCompanyIds);
    }
  }, [options, selectedCompanyIds, rawSelected, setSelectedCompanyIds]);

  const companySelectionKey = useMemo(
    () =>
      selectedCompanyIds
        .slice()
        .sort((a, b) => a - b)
        .join(','),
    [selectedCompanyIds],
  );

  return (
    <CompanyFilterContext.Provider
      value={{
        selectedCompanyIds,
        companySelectionKey,
        setSelectedCompanyIds,
        availableCompanies: options,
        showSelector: options.length > 1,
      }}
    >
      {children}
    </CompanyFilterContext.Provider>
  );
};

const SAFE_FALLBACK: CompanyFilterContextType = {
  selectedCompanyIds: [],
  companySelectionKey: '',
  setSelectedCompanyIds: () => {},
  availableCompanies: [],
  showSelector: false,
};

export const useCompanyFilter = () => {
  const context = useContext(CompanyFilterContext);
  // Fallback seguro p/ componentes fora do provider (ex.: portal externo)
  if (context === undefined) return SAFE_FALLBACK;
  return context;
};
